import { cn } from "@/common/utils/cn"
import { VariantProps } from "class-variance-authority"
import { buttonVariants } from "./styles"
import { forwardRef, useId } from "react"
import Spinner from "../Spinner";
import useUserPermissions from "../../hooks/useUserPermissions";
import { Tooltip, type PlacesType } from "react-tooltip";

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  disabled?: boolean
  name?: string
  type?: "button" | "submit" | "reset"
  isLoading?: boolean
  spinnerClassName?: string
  tooltipPlace?: PlacesType;
  tooltipMessage?: string;
  noPermissionText?: string
  operationKlassToCheckPermission?: string
  propertiesToCheckPermission?: string[];
  dataTestId?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant,
  name,
  className,
  disabled = false,
  isLoading = false,
  type = 'button',
  spinnerClassName,
  tooltipPlace = "left",
  tooltipMessage,
  noPermissionText = 'Você não tem permissão',
  operationKlassToCheckPermission,
  propertiesToCheckPermission,
  dataTestId,
  ...props
}, ref) => {
  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();
  const tooltipId = useId();

  const isPermissionNeeded = !!operationKlassToCheckPermission;
  const hasPermission = isPermissionNeeded && hasUserPermission(operationKlassToCheckPermission, propertiesToCheckPermission);

  return (
    <>
      <button
        className={cn(buttonVariants({ variant }), className, (!!disabled || isLoading || (isPermissionNeeded && !hasPermission)) ? 'opacity-50 cursor-default' : '')}
        data-testid={dataTestId || (name + '_button')}
        data-tooltip-id={((isPermissionNeeded && !hasPermission) || tooltipMessage) ? tooltipId : ''}
        type={type}
        ref={ref}
        {...props}
        disabled={disabled || (isPermissionNeeded && !hasPermission)}>
        {children}

        {(isLoading || (isPermissionNeeded && isUserPermissionsPending)) && <Spinner className={cn("w-4 h-4 fill-white-800", spinnerClassName)} />}
      </button>

      {((isPermissionNeeded && !hasPermission && !isUserPermissionsPending) || tooltipMessage) && (
        <Tooltip id={tooltipId} place={tooltipPlace}>{tooltipMessage || noPermissionText}</Tooltip>
      )}
    </>
  )
})
Button.displayName = "Button"

export default Button
